import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faClipboard } from '@fortawesome/pro-solid-svg-icons/faClipboard';

import { useMemo, useCallback } from 'react';
import { useIntl, FormattedMessage } from 'react-intl';
import { Text, TextType } from 'ui-components/components/text';
import { FlexColumn, FlexRow } from 'ui-components/components/flex';
import { Gap } from 'ui-components/components/flex/gap';
import { Button } from 'ui-components/components/button';
import { NumberInput } from 'ui-components/components/number-input';
import { FloatTextArea } from 'ui-components/components/float-text-area';
import { Tooltip } from 'ui-components/components/tooltip';
import { TooltipContent } from 'ui-components/components/tooltip/tooltip-content';
import { LoadingIndicator } from 'ui-components/components/loading-indicator';
import ShippingServiceSelector from '../../../../../../components/shipping-service-selector';
import PackageDropdown from '../../../../../../components/package-dropdown/container';
import useServicesFromPrimaryProviders from '../../../../../../components/shipping-service-selector/service-id-hooks/use-services-from-primary-providers';
import useSelectedItemName from '../../../../../../components/shipping-service-selector/use-selected-item-name';
import useCarrierGroups from '../../../../../../components/shipping-service-selector/group-hooks/use-carrier-groups';
import { localDeliveryBlacklistFilterCB } from '../../../../../../fulfillment-planner/context/hooks';
import { filterByServices } from '../../../../../settings/stores/selected-store/helpers/service-filter';
import { useBrandedReturnsContext } from '../../context/branded-returns-context';
import { messages } from './messages';
import style from './style.module.less';

const TEXT_INPUT_MAX_LENGTH = 500;

export const BrandedReturnsForm = () => {
  const intl = useIntl();
  const { formData, updateFormData, errors, selectedPackage, copyUrlToClipboard, isLoadingStore, storeError } = useBrandedReturnsContext();

  const availableServices = useServicesFromPrimaryProviders();
  const filteredAvailableServices = availableServices.filter(localDeliveryBlacklistFilterCB);

  const selectedServiceItem = useMemo(
    () => ({
      serviceId: formData.shippingServiceId?.toString(),
    }),
    [formData.shippingServiceId],
  );

  const selectedItemName = useSelectedItemName(selectedServiceItem);
  const carrierGroups = useCarrierGroups(filteredAvailableServices, selectedServiceItem);
  const customPackagesOnly = (pkg) => !!pkg.isCustom;
  const packageFilter = formData.shippingServiceId
    ? filterByServices(availableServices, formData.shippingServiceId.toString())
    : customPackagesOnly;

  const handleMaxDaysChange = useCallback(
    (value) => {
      updateFormData('maxDaysToAllowForReturn', value);
    },
    [updateFormData],
  );

  const handleServiceChange = useCallback(
    (updateObject) => {
      updateFormData('shippingServiceId', updateObject.serviceId ?? null);
    },
    [updateFormData],
  );

  const handlePackageChange = useCallback(
    (packageType) => {
      updateFormData('packageTypeId', packageType?.packageTypeId ?? null);
    },
    [updateFormData],
  );

  const handlePolicyMessageChange = useCallback(
    (value) => {
      updateFormData('returnPolicyMessage', value);
    },
    [updateFormData],
  );

  const handleEmailMessageChange = useCallback(
    (value) => {
      updateFormData('returnEmailMessage', value);
    },
    [updateFormData],
  );

  return (
    <FlexColumn className={style['branded-returns-modal']} gap={Gap.Large}>
      {errors && errors.length > 0 && (
        <div className={style['error-container']}>
          {errors.map((error, index) => (
            <div key={index} className={style['error-message']}>
              {error}
            </div>
          ))}
        </div>
      )}

      {storeError && (
        <div className={style['error-container']}>
          <div className={style['error-message']}>{storeError}</div>
        </div>
      )}

      {isLoadingStore && (
        <FlexRow gap={Gap.Small}>
          <LoadingIndicator className={style['loading-indicator']} />
          <Text value={intl.formatMessage(messages.loadingStoreInformation)} />
        </FlexRow>
      )}

      <FlexColumn gap={Gap.Medium}>
        <Text textType={TextType.H3} value={messages.returnExceptionsTitle} />
        <FlexRow className={style['exception-text']}>
          <Text value={messages.allowCustomersText} />
          <NumberInput min={1} onChange={handleMaxDaysChange} value={formData.maxDaysToAllowForReturn} />
          <Text value={messages.daysOfOrderShipDate} />
        </FlexRow>
      </FlexColumn>
      <FlexColumn className={style['section-seperator']} />
      <FlexColumn gap={Gap.Medium}>
        <Text textType={TextType.H3} value={messages.returnServiceAndPackageTitle} />
        <Text textType={TextType.Paragraph} value={messages.returnServiceDescription} />

        <FlexColumn className={style['service-dropdown']} gap={Gap.Small}>
          <Text textType={TextType.Label} value={messages.service} />
          <ShippingServiceSelector
            defaultEmptyText={messages.sameAsOutbound}
            onSelected={handleServiceChange}
            selectedItemName={selectedItemName}
            serviceGroups={carrierGroups}
          />
        </FlexColumn>

        <FlexColumn gap={Gap.Small}>
          <Text textType={TextType.Label} value={messages.package} />
          <PackageDropdown
            className={style['package-dropdown']}
            defaultEmptyText={<FormattedMessage {...messages.sameAsOutbound} />}
            dropdownMenuShouldHaveWidthOfButton
            onPackageSelected={handlePackageChange}
            packageFilter={packageFilter}
            selectedPackageKey={selectedPackage?.packageId}
            simpleToggler
            variant='form'
          />
        </FlexColumn>
      </FlexColumn>
      <FlexColumn className={style['section-seperator']} />
      <FlexColumn gap={Gap.Medium}>
        <Text textType={TextType.H3} value={messages.brandedReturnsPortalUrlTitle} />
        <FlexRow className={style['url-tool']} gap={Gap.Small}>
          <Text textType={TextType.Label} value={messages.urlLabel} />
          <div className={style['return-url']} id='branded-returns-url'>
            {formData.brandedReturnsPortalUrl}
          </div>
          <Tooltip>
            <Button className={style['copy-button']} onClick={copyUrlToClipboard} theme='primary'>
              <FontAwesomeIcon icon={faClipboard} />
            </Button>
            <TooltipContent>
              <FormattedMessage {...messages.copyToClipboard} />
            </TooltipContent>
          </Tooltip>
        </FlexRow>
      </FlexColumn>
      <FlexColumn className={style['section-seperator']} />
      <FlexColumn gap={Gap.Medium}>
        <Text textType={TextType.H3} value={messages.returnPolicyMessageTitle} />
        <Text textType={TextType.Paragraph} value={messages.returnPolicyMessageDescription} />
        <FloatTextArea
          label={intl.formatMessage(messages.policyMessageLabel)}
          maxLength={TEXT_INPUT_MAX_LENGTH}
          onChange={handlePolicyMessageChange}
          value={formData.returnPolicyMessage}
        />
      </FlexColumn>
      <FlexColumn className={style['section-seperator']} />
      <FlexColumn gap={Gap.Medium}>
        <Text textType={TextType.H3} value={messages.returnEmailMessageTitle} />
        <Text textType={TextType.Paragraph} value={messages.returnEmailMessageDescription} />
        <FloatTextArea
          label={intl.formatMessage(messages.emailMessageLabel)}
          maxLength={TEXT_INPUT_MAX_LENGTH}
          onChange={handleEmailMessageChange}
          value={formData.returnEmailMessage}
        />
      </FlexColumn>
    </FlexColumn>
  );
};
